import React, { useState, useEffect } from 'react';
import { 
  Layout, 
  Typography, 
  Card, 
  Table, 
  Input, 
  Button, 
  Space, 
  Tag, 
  Drawer, 
  Descriptions, 
  Avatar, 
  Statistic, 
  Row, 
  Col,
  message,
  Spin
} from 'antd';
import { 
  SearchOutlined, 
  UserOutlined, 
  EyeOutlined, 
  PhoneOutlined,
  MailOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import axios from 'axios';
import { authService } from '../services/auth';

const { Content } = Layout;
const { Title } = Typography;
const { Search } = Input;

interface User {
  id: string;
  phone: string;
  email?: string;
  name?: string;
  surname?: string;
  created_at: string;
  updated_at: string;
  is_active: boolean;
  verification_status: 'pending' | 'verified' | 'failed';
  last_login?: string;
}

interface UserDetail {
  user: User;
  identityPhotos: any[];
  programIdentities: any[];
}

const UsersPage: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });
  const [selectedUser, setSelectedUser] = useState<UserDetail | null>(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    pendingVerifications: 0,
    verificationRate: '0'
  });

  // Kullanıcıları getir
  const fetchUsers = async (page = 1, search = '') => {
    setLoading(true);
    try {
      console.log('👥 Fetching users...', { page, search });
      const api = authService.getApiInstance();
      const params = new URLSearchParams({
        page: page.toString(),
        limit: pagination.pageSize.toString(),
        ...(search && { search })
      });

      const response = await api.get(`/api/data/users?${params}`, {
        timeout: 30000 // 30 seconds
      });

      if (response.data.success) {
        setUsers(response.data.data.users);
        setPagination(prev => ({
          ...prev,
          current: page,
          total: response.data.data.total
        }));
      }
    } catch (error: any) {
      message.error('Kullanıcılar yüklenirken hata oluştu: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Dashboard istatistikleri getir
  const fetchStats = async () => {
    try {
      console.log('📊 Fetching user stats...');
      const api = authService.getApiInstance();
      const response = await api.get('/api/data/dashboard/stats', {
        timeout: 30000 // 30 seconds
      });

      if (response.data.success) {
        setStats(response.data.data.stats);
        console.log('✅ User stats loaded:', response.data.data.stats);
      }
    } catch (error: any) {
      console.error('❌ User stats fetch error:', error);
    }
  };

  // Kullanıcı detaylarını getir
  const fetchUserDetails = async (userId: string) => {
    try {
      console.log('👤 Fetching user details for:', userId);
      const api = authService.getApiInstance();
      const response = await api.get(`/api/data/users/${userId}`, {
        timeout: 30000 // 30 seconds
      });

      if (response.data.success) {
        setSelectedUser(response.data.data);
        setDrawerVisible(true);
      }
    } catch (error: any) {
      message.error('Kullanıcı detayları yüklenirken hata oluştu: ' + error.message);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchStats();
  }, []);

  // Arama işlemi
  const handleSearch = (value: string) => {
    setSearchText(value);
    fetchUsers(1, value);
  };

  // Sayfa değişimi
  const handleTableChange = (page: number) => {
    fetchUsers(page, searchText);
  };

  // Doğrulama durumu rengi
  const getVerificationColor = (status: string) => {
    switch (status) {
      case 'verified': return 'green';
      case 'pending': return 'orange';
      case 'failed': return 'red';
      default: return 'default';
    }
  };

  // Doğrulama durumu ikonu
  const getVerificationIcon = (status: string) => {
    switch (status) {
      case 'verified': return <CheckCircleOutlined />;
      case 'pending': return <ExclamationCircleOutlined />;
      case 'failed': return <CloseCircleOutlined />;
      default: return null;
    }
  };

  const columns: ColumnsType<User> = [
    {
      title: 'Kullanıcı',
      key: 'user',
      render: (_, record) => (
        <Space>
          <Avatar icon={<UserOutlined />} />
          <div>
            <div style={{ fontWeight: 'bold' }}>
              {(() => {
                // Real data structure: name="RASİM ÇETİN", surname=""
                if (record.name && record.surname && record.surname.trim()) {
                  return `${record.name} ${record.surname}`;
                }
                // If name exists but surname is empty (like our case)
                if (record.name && record.name.trim()) {
                  return record.name;
                }
                // Fallback alternatives
                if (record.first_name && record.last_name && record.last_name.trim()) {
                  return `${record.first_name} ${record.last_name}`;
                }
                if (record.first_name && record.first_name.trim()) {
                  return record.first_name;
                }
                if (record.full_name && record.full_name.trim()) {
                  return record.full_name;
                }
                if (record.username && record.username.trim()) {
                  return record.username;
                }
                if (record.email) {
                  return record.email.split('@')[0];
                }
                
                return 'İsim Belirtilmemiş';
              })()}
            </div>
            <div style={{ fontSize: '12px', color: '#666' }}>
              ID: {record.id.substring(0, 8)}...
            </div>
          </div>
        </Space>
      ),
    },
    {
      title: 'İletişim',
      key: 'contact',
      render: (_, record) => (
        <div>
          <div><PhoneOutlined /> {record.phone}</div>
          {record.email && (
            <div style={{ fontSize: '12px', color: '#666' }}>
              <MailOutlined /> {record.email}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Durum',
      key: 'status',
      render: (_, record) => (
        <Space direction="vertical" size="small">
          <Tag color={record.is_active ? 'green' : 'red'}>
            {record.is_active ? 'Aktif' : 'Pasif'}
          </Tag>
          <Tag 
            color={getVerificationColor(record.verification_status)}
            icon={getVerificationIcon(record.verification_status)}
          >
            {record.verification_status === 'verified' && 'Doğrulandı'}
            {record.verification_status === 'pending' && 'Beklemede'}
            {record.verification_status === 'failed' && 'Başarısız'}
          </Tag>
        </Space>
      ),
    },
    {
      title: 'Kayıt Tarihi',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => (
        <div>
          <CalendarOutlined /> {new Date(date).toLocaleDateString('tr-TR')}
          <div style={{ fontSize: '12px', color: '#666' }}>
            {new Date(date).toLocaleTimeString('tr-TR')}
          </div>
        </div>
      ),
    },
    {
      title: 'Son Giriş',
      dataIndex: 'last_login',
      key: 'last_login',
      render: (date) => date ? (
        <div>
          {new Date(date).toLocaleDateString('tr-TR')}
          <div style={{ fontSize: '12px', color: '#666' }}>
            {new Date(date).toLocaleTimeString('tr-TR')}
          </div>
        </div>
      ) : (
        <span style={{ color: '#999' }}>Hiç giriş yapmamış</span>
      ),
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_, record) => (
        <Button 
          type="primary" 
          icon={<EyeOutlined />}
          onClick={() => fetchUserDetails(record.id)}
        >
          Detay
        </Button>
      ),
    },
  ];

  return (
    <Content style={{ margin: '24px 16px', padding: 24, minHeight: 280 }}>
      <Title level={2}>👥 Kullanıcı Yönetimi</Title>
      
      {/* İstatistikler */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Toplam Kullanıcı"
              value={stats.totalUsers}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Aktif Kullanıcı"
              value={stats.activeUsers}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Bekleyen Doğrulama"
              value={stats.pendingVerifications}
              prefix={<ExclamationCircleOutlined />}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Doğrulama Oranı"
              value={stats.verificationRate}
              suffix="%"
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Arama ve Filtreler */}
      <Card style={{ marginBottom: 16 }}>
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Search
            placeholder="Telefon, email, isim ile ara..."
            allowClear
            enterButton={<SearchOutlined />}
            size="large"
            style={{ width: 400 }}
            onSearch={handleSearch}
          />
          <Button onClick={() => fetchUsers(1, searchText)}>
            Yenile
          </Button>
        </Space>
      </Card>

      {/* Kullanıcılar Tablosu */}
      <Card>
        <Table
          columns={columns}
          dataSource={users}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: false,
            showQuickJumper: true,
            showTotal: (total, range) => 
              `${range[0]}-${range[1]} / ${total} kullanıcı`,
            onChange: handleTableChange
          }}
        />
      </Card>

      {/* Kullanıcı Detay Drawer */}
      <Drawer
        title="Kullanıcı Detayları"
        placement="right"
        size="large"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
      >
        {selectedUser && (
          <div>
            <Descriptions title="Temel Bilgiler" bordered column={1}>
              <Descriptions.Item label="ID">
                {selectedUser.user.id}
              </Descriptions.Item>
              <Descriptions.Item label="Ad Soyad">
                {selectedUser.user.name || selectedUser.user.surname 
                  ? `${selectedUser.user.name || ''} ${selectedUser.user.surname || ''}`.trim()
                  : 'Belirtilmemiş'}
              </Descriptions.Item>
              <Descriptions.Item label="Telefon">
                {selectedUser.user.phone}
              </Descriptions.Item>
              <Descriptions.Item label="Email">
                {selectedUser.user.email || 'Belirtilmemiş'}
              </Descriptions.Item>
              <Descriptions.Item label="Durum">
                <Tag color={selectedUser.user.is_active ? 'green' : 'red'}>
                  {selectedUser.user.is_active ? 'Aktif' : 'Pasif'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Doğrulama">
                <Tag 
                  color={getVerificationColor(selectedUser.user.verification_status)}
                  icon={getVerificationIcon(selectedUser.user.verification_status)}
                >
                  {selectedUser.user.verification_status === 'verified' && 'Doğrulandı'}
                  {selectedUser.user.verification_status === 'pending' && 'Beklemede'}
                  {selectedUser.user.verification_status === 'failed' && 'Başarısız'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Kayıt Tarihi">
                {new Date(selectedUser.user.created_at).toLocaleString('tr-TR')}
              </Descriptions.Item>
              <Descriptions.Item label="Son Güncelleme">
                {new Date(selectedUser.user.updated_at).toLocaleString('tr-TR')}
              </Descriptions.Item>
              <Descriptions.Item label="Son Giriş">
                {selectedUser.user.last_login 
                  ? new Date(selectedUser.user.last_login).toLocaleString('tr-TR')
                  : 'Hiç giriş yapmamış'}
              </Descriptions.Item>
            </Descriptions>

            <Title level={4} style={{ marginTop: 24 }}>
              Kimlik Fotoğrafları ({selectedUser.identityPhotos.length})
            </Title>
            {selectedUser.identityPhotos.length > 0 ? (
              <div>
                {selectedUser.identityPhotos.map((photo, index) => (
                  <Card key={index} size="small" style={{ marginBottom: 8 }}>
                    <Space>
                      <Tag>{photo.photo_type}</Tag>
                      <Tag color={getVerificationColor(photo.verification_status)}>
                        {photo.verification_status}
                      </Tag>
                      <span>{new Date(photo.created_at).toLocaleString('tr-TR')}</span>
                    </Space>
                  </Card>
                ))}
              </div>
            ) : (
              <p>Kimlik fotoğrafı bulunmuyor.</p>
            )}

            <Title level={4} style={{ marginTop: 24 }}>
              Kimlik Doğrulama ({selectedUser.programIdentities.length})
            </Title>
            {selectedUser.programIdentities.length > 0 ? (
              <div>
                {selectedUser.programIdentities.map((identity, index) => (
                  <Card key={index} size="small" style={{ marginBottom: 8 }}>
                    <Descriptions size="small" column={1}>
                      <Descriptions.Item label="TC No">
                        {identity.identity_number || 'Belirtilmemiş'}
                      </Descriptions.Item>
                      <Descriptions.Item label="Ad Soyad">
                        {identity.name && identity.surname 
                          ? `${identity.name} ${identity.surname}`
                          : 'Belirtilmemiş'}
                      </Descriptions.Item>
                      <Descriptions.Item label="Doğum Tarihi">
                        {identity.birth_date 
                          ? new Date(identity.birth_date).toLocaleDateString('tr-TR')
                          : 'Belirtilmemiş'}
                      </Descriptions.Item>
                      <Descriptions.Item label="Yöntem">
                        <Tag>{identity.verification_method}</Tag>
                      </Descriptions.Item>
                      <Descriptions.Item label="Durum">
                        <Tag color={getVerificationColor(identity.verification_status)}>
                          {identity.verification_status}
                        </Tag>
                      </Descriptions.Item>
                    </Descriptions>
                  </Card>
                ))}
              </div>
            ) : (
              <p>Kimlik doğrulama kaydı bulunmuyor.</p>
            )}
          </div>
        )}
      </Drawer>
    </Content>
  );
};

export default UsersPage;